import {
  ArrowUpIcon,
  MicrophoneIcon,
  PlusIcon,
  StopCircleIcon,
  WaveformIcon,
} from "@phosphor-icons/react";
import type { MutableRefObject, Ref } from "react";
import {
  type ChangeEvent,
  type ClipboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  BriefingModeMenu,
  type BriefingModeMenuProps,
} from "@/components/ui/chat/BriefingModeMenu";
import {
  attachmentFileKey,
  ChatInputAttachmentList,
  isImageAttachmentFile,
} from "@/components/ui/chat/ChatInputAttachmentList";
import { IconButton } from "@/components/ui/icon-button";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

const assignRef = <T,>(instanceRef: Ref<T> | undefined, value: T | null) => {
  if (instanceRef == null) return;
  if (typeof instanceRef === "function") {
    instanceRef(value);
  } else {
    (instanceRef as MutableRefObject<T | null>).current = value;
  }
};

export const INPUT_LENGTH_MAX = 32_000;

/** Backend limit for the full encoded chat request (text + base64 attachments). */
export const CHAT_ENCODED_REQUEST_LIMIT_BYTES = 20 * 1024 * 1024;

const CHAT_ENCODED_REQUEST_LIMIT_HEADLINE =
  "Message and attachments exceed the 20 MB send limit.";

/**
 * Rough upper bound on encoded request size (base64 files + UTF-8 text + framing).
 * @param text - Composer text included in the request.
 * @param files - Attached files to encode.
 */
export const estimateEncodedRequestBytes = (
  text: string,
  files: FileList | Iterable<File>,
): number => {
  let total = new TextEncoder().encode(text).length;
  for (const file of files) {
    total += Math.ceil((file.size * 4) / 3);
  }
  return total + 1024;
};

export const appendDictatedPhrase = (
  prev: string,
  phrase: string,
): { value: string; truncated: boolean } => {
  const trimmed = prev.trimEnd();
  const sep = trimmed.length > 0 ? " " : "";
  const next = `${trimmed}${sep}${phrase}`;
  if (next.length > INPUT_LENGTH_MAX) {
    return { value: next.slice(0, INPUT_LENGTH_MAX), truncated: true };
  }
  return { value: next, truncated: false };
};

export const notifyDictationTruncated = (): void => {
  toast.warning(
    `Dictation truncated at ${INPUT_LENGTH_MAX.toLocaleString()}-character limit`,
  );
};

const DEFAULT_PLACEHOLDER = "Ask anything";
const LANDING_PLACEHOLDER = "Ask anything about your business. Get a briefing.";
const STUDIO_PLACEHOLDER = "Describe the template you want…";

const CONVERSATION_AI_DISCLAIMER =
  "Handraise AI Chat can make mistakes. Please double-check responses.";

/** Landing/conversation share Herald chrome; studio matches Template Studio sidebar. */
const VARIANT_LAYOUT = {
  landing: { width: 700, minHeight: 120, maxRows: 10 },
  conversation: { width: 768, minHeight: 80, maxRows: 10 },
  studio: { width: "100%", minHeight: 0, maxRows: 10 },
} as const;

export type ChatInputVariant = keyof typeof VARIANT_LAYOUT;

const toCssLength = (v: number | string): string =>
  typeof v === "number" ? `${v}px` : v;

const outerShellBase = "shrink-0 overflow-visible bg-white pb-5";
const composerBase = cn(
  "flex border bg-white",
  "transition-[border-color,box-shadow] duration-300",
  "focus-within:border-violet-700 focus-within:ring-violet-700 focus-within:animate-none",
);

const textareaBase = cn(
  "min-h-0 resize-none bg-transparent text-sm text-gray-800",
  "placeholder:text-gray-400 focus:outline-none",
);

const trailingActionsClass =
  "ml-auto flex min-w-0 shrink-0 items-center overflow-visible";
const actionIconClass = "w-8 h-8 p-0";

const DEFAULT_FILE_ACCEPT = "application/pdf,text/csv,.csv";
const IMAGE_FILE_ACCEPT = "image/png,image/jpeg,.png,.jpg,.jpeg";
const IMAGES_ONLY_FILE_ACCEPT = `${IMAGE_FILE_ACCEPT},image/webp,.webp,image/svg+xml,.svg`;

export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text?: string) => void;
  onStop: () => void;
  isStreaming: boolean;
  isRecording?: boolean;
  showMicButton?: boolean;
  onMicPress?: () => void;
  autoFocus?: boolean;
  placeholder?: string;
  width?: number | string;
  minHeight?: number | string;
  ref?: Ref<HTMLTextAreaElement>;
  variant?: ChatInputVariant;
  files?: FileList;
  onFilesChange?: (files: FileList | undefined) => void;
  /** When true, the attach button also accepts PNG/JPG images. */
  allowImages?: boolean;
  /** When true, the attach button accepts only PNG, JPG, WebP and SVG images. */
  imagesOnly?: boolean;
  briefingMenu?: BriefingModeMenuProps;
  /** Shown below the composer when `variant` is `studio`. */
  footerNote?: string;
  /** Character limit for typing, paste and submission; defaults to 32,000. */
  maxLength?: number;
}

const ChatInput = ({
  ref,
  value,
  onChange,
  onSubmit,
  onStop,
  isStreaming,
  isRecording = false,
  showMicButton = true,
  onMicPress,
  autoFocus = true,
  placeholder,
  variant = "conversation",
  width: widthProp,
  minHeight: minHeightProp,
  files,
  onFilesChange,
  allowImages: allowImagesProp = false,
  imagesOnly = false,
  briefingMenu,
  footerNote,
  maxLength = INPUT_LENGTH_MAX,
}: ChatInputProps) => {
  const allowImages = allowImagesProp || imagesOnly;
  const isStudio = variant === "studio";
  const resolvedPlaceholder =
    placeholder ??
    (variant === "landing"
      ? LANDING_PLACEHOLDER
      : variant === "studio"
        ? STUDIO_PLACEHOLDER
        : DEFAULT_PLACEHOLDER);

  const layout = VARIANT_LAYOUT[variant];
  const width = widthProp ?? layout.width;
  const minHeight = minHeightProp ?? layout.minHeight;
  const hasFiles = files != null && files.length > 0;
  const attachmentsWithinLimit =
    !hasFiles ||
    estimateEncodedRequestBytes(value, files) <=
      CHAT_ENCODED_REQUEST_LIMIT_BYTES;
  const hasText = value.trim().length > 0;
  const canSend =
    (isStudio ? hasText : hasText || hasFiles) &&
    attachmentsWithinLimit &&
    value.length <= maxLength &&
    !isStreaming &&
    !isRecording;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileAccept = imagesOnly
    ? IMAGES_ONLY_FILE_ACCEPT
    : allowImages
      ? `${DEFAULT_FILE_ACCEPT},${IMAGE_FILE_ACCEPT}`
      : DEFAULT_FILE_ACCEPT;

  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!allowImages || files == null) {
      setImagePreviewUrls((previous) =>
        previous.length === 0 ? previous : [],
      );
      return;
    }

    const urls = Array.from(files).map((file) =>
      isImageAttachmentFile(file) ? URL.createObjectURL(file) : "",
    );
    setImagePreviewUrls(urls);

    return () => {
      for (const url of urls) {
        if (url) URL.revokeObjectURL(url);
      }
    };
  }, [allowImages, files]);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files;
      if (!selected || selected.length === 0) {
        e.target.value = "";
        return;
      }

      const dt = new DataTransfer();
      const existingKeys = new Set<string>();

      if (files != null) {
        for (const file of Array.from(files)) {
          dt.items.add(file);
          existingKeys.add(attachmentFileKey(file));
        }
      }

      // `accept` only filters the picker; "All files" can still pick a PDF.
      const accepted = Array.from(selected).filter(
        (file) =>
          !imagesOnly ||
          ["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(
            file.type,
          ) ||
          /\.(png|jpe?g|webp|svg)$/i.test(file.name),
      );
      if (accepted.length < selected.length) {
        toast.warning("Only PNG, JPEG, WebP and SVG images can be attached.");
      }

      for (const file of accepted) {
        const key = attachmentFileKey(file);
        if (existingKeys.has(key)) continue;
        dt.items.add(file);
        existingKeys.add(key);
      }

      if (
        dt.files.length > 0 &&
        estimateEncodedRequestBytes(value, dt.files) >
          CHAT_ENCODED_REQUEST_LIMIT_BYTES
      ) {
        toast.warning(
          `${CHAT_ENCODED_REQUEST_LIMIT_HEADLINE} Remove a file or send a shorter message.`,
        );
        e.target.value = "";
        return;
      }

      onFilesChange?.(dt.files.length > 0 ? dt.files : undefined);
      e.target.value = "";
    },
    [files, imagesOnly, onFilesChange, value],
  );

  const handleRemoveFile = useCallback(
    (fileToRemove?: File) => {
      if (!fileToRemove || files == null) {
        onFilesChange?.(undefined);
        return;
      }

      const dt = new DataTransfer();
      for (const file of Array.from(files)) {
        if (file !== fileToRemove) dt.items.add(file);
      }
      onFilesChange?.(dt.files.length > 0 ? dt.files : undefined);
    },
    [files, onFilesChange],
  );
  const showStop = isStreaming || isRecording;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) onSubmit();
      }
    },
    [canSend, onSubmit],
  );

  const handleTextareaChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      if (e.target.value.length <= maxLength) {
        onChange(e.target.value);
      }
    },
    [onChange, maxLength],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const target = e.currentTarget;
      const pasted = e.clipboardData.getData("text");
      const selectionLength = target.selectionEnd - target.selectionStart;
      const projectedLength = value.length - selectionLength + pasted.length;
      if (projectedLength > maxLength) {
        e.preventDefault();
        toast.warning(
          `Paste exceeded the ${maxLength.toLocaleString()}-character limit`,
        );
      }
    },
    [value.length, maxLength],
  );

  const mergedTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      assignRef(ref, node);
    },
    [ref],
  );

  if (isStudio) {
    return (
      <div className="w-full min-w-0 shrink-0 border-t border-gray-100 p-3">
        <div
          className={cn(
            "rounded-[10px] border border-gray-200 bg-white",
            "transition-[border-color,box-shadow] duration-150",
            "focus-within:border-violet-700 focus-within:shadow-[0_0_0_3px_rgba(109,40,217,0.12)]",
            hasFiles && "border-violet-700",
            isStreaming && "animate-chat-input-border-stream",
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={fileAccept}
            multiple
            className="hidden"
            onChange={handleFileChange}
          />

          {hasFiles ? (
            <ChatInputAttachmentList
              files={files}
              allowImages={allowImages}
              imagePreviewUrls={imagePreviewUrls}
              attachmentsWithinLimit={attachmentsWithinLimit}
              onRemoveFile={handleRemoveFile}
              className="px-3"
            />
          ) : null}

          <TextareaAutosize
            ref={mergedTextareaRef}
            value={value}
            onChange={handleTextareaChange}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={resolvedPlaceholder}
            minRows={2}
            maxRows={VARIANT_LAYOUT.studio.maxRows}
            className="block w-full min-h-0 resize-none bg-transparent px-3 pt-2.5 pb-1 text-[13px] leading-5 text-gray-800 placeholder:text-gray-400 focus:outline-none"
            autoFocus={autoFocus}
          />
          <div className="flex items-center gap-1 px-2 pb-2">
            {onFilesChange ? (
              <IconButton
                icon={<PlusIcon className="text-gray-600" size={16} />}
                variant="ghost"
                size="sm"
                aria-label="Attach file"
                tooltipLabel="Attach file"
                tooltipPosition="top"
                onPress={() => fileInputRef.current?.click()}
                isDisabled={showStop}
                className="size-7 p-0"
              />
            ) : null}
            <div className="ml-auto flex items-center">
              {showStop ? (
                <IconButton
                  icon={
                    <StopCircleIcon className="text-gray-800" weight="fill" />
                  }
                  variant="outline"
                  size="sm"
                  aria-label={
                    isRecording ? "Stop recording" : "Stop generating"
                  }
                  tooltipLabel={
                    isRecording ? "Stop recording" : "Stop generating"
                  }
                  tooltipPosition="top"
                  onPress={onStop}
                  className="size-7 p-0"
                />
              ) : (
                <IconButton
                  icon={<ArrowUpIcon size={14} />}
                  variant="default"
                  size="sm"
                  aria-label="Send message"
                  tooltipLabel="Send message"
                  tooltipPosition="top"
                  onPress={() => onSubmit()}
                  isDisabled={!canSend}
                  className={cn(
                    "size-7 p-0",
                    !canSend &&
                      "bg-gray-100 text-gray-400 hover:bg-gray-100 active:bg-gray-100",
                  )}
                />
              )}
            </div>
          </div>
        </div>
        {footerNote ? (
          <p className="mt-2 px-1 text-[11px] text-gray-400" role="note">
            {footerNote}
          </p>
        ) : null}
      </div>
    );
  }

  const isConversation = variant === "conversation";
  const inputWidth = toCssLength(width);

  const composerClassName = cn(
    composerBase,
    hasFiles && "border-violet-700",
    isStreaming && "animate-chat-input-border-stream",
    "w-full min-w-0 max-w-full flex-col overflow-visible rounded-[14px]",
  );

  const minHeightPx =
    typeof minHeight === "number" ? minHeight : Number.parseFloat(minHeight);
  const resolvedMinHeightPx = Number.isFinite(minHeightPx)
    ? minHeightPx
    : layout.minHeight;

  const effectiveMinHeight = hasFiles
    ? Math.max(resolvedMinHeightPx, isConversation ? 90 : 130)
    : minHeight;
  const composerStyle = { minHeight: toCssLength(effectiveMinHeight) };

  const textareaClassName = cn(
    textareaBase,
    hasFiles
      ? "min-w-0 shrink-0 px-4 pb-1 pt-2 leading-5"
      : "min-w-0 w-full shrink-0 px-4 pb-1 pt-3 leading-5",
  );

  const attachmentButton = onFilesChange ? (
    <div className="flex shrink-0 items-center">
      <IconButton
        icon={<PlusIcon className="text-gray-600" />}
        variant="ghost"
        size="sm"
        aria-label="Attach file"
        tooltipLabel="Attach file"
        tooltipPosition="top"
        onPress={() => fileInputRef.current?.click()}
        isDisabled={showStop}
        className={actionIconClass}
      />
    </div>
  ) : null;

  const composerBottomRow = (
    <div className="flex w-full shrink-0 items-center gap-2 px-4 pb-2 pt-1">
      {attachmentButton}
      <div className={trailingActionsClass}>
        {briefingMenu ? <BriefingModeMenu {...briefingMenu} /> : null}
        {showMicButton ? (
          <IconButton
            icon={
              isRecording ? (
                <WaveformIcon className="text-gray-600" />
              ) : (
                <MicrophoneIcon className="text-gray-600" />
              )
            }
            variant="ghost"
            aria-label={
              isRecording ? "Dictating — use Stop to finish" : "Dictate"
            }
            tooltipLabel={
              isRecording ? "Dictating — use Stop to finish" : "Dictate"
            }
            tooltipPosition="top"
            onPress={onMicPress}
            isDisabled={onMicPress == null || showStop}
            className={cn(actionIconClass, "mr-2.5")}
          />
        ) : null}
        {showStop ? (
          <IconButton
            icon={<StopCircleIcon className="text-gray-800" weight="fill" />}
            variant="outline"
            aria-label={isRecording ? "Stop recording" : "Stop generating"}
            tooltipLabel={isRecording ? "Stop recording" : "Stop generating"}
            tooltipPosition="top"
            onPress={onStop}
            className={actionIconClass}
          />
        ) : (
          <IconButton
            icon={<ArrowUpIcon size={14} />}
            variant="default"
            aria-label="Send message"
            tooltipLabel="Send message"
            tooltipPosition="top"
            onPress={() => onSubmit()}
            isDisabled={!canSend}
            className={actionIconClass}
          />
        )}
      </div>
    </div>
  );

  const textareaMaxRows = layout.maxRows;

  const textarea = (
    <TextareaAutosize
      ref={mergedTextareaRef}
      value={value}
      onChange={handleTextareaChange}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      placeholder={resolvedPlaceholder}
      minRows={1}
      maxRows={textareaMaxRows}
      className={textareaClassName}
      autoFocus={autoFocus}
    />
  );

  return (
    <div
      className={cn(outerShellBase, "w-full min-w-0 max-w-full px-2 sm:px-6")}
    >
      <div
        className="mx-auto box-border w-full min-w-0 shrink-0 overflow-visible sm:min-w-[300px]"
        style={
          isConversation ? { width: inputWidth } : { maxWidth: inputWidth }
        }
      >
        <div className={composerClassName} style={composerStyle}>
          <input
            ref={fileInputRef}
            type="file"
            accept={fileAccept}
            multiple
            className="hidden"
            onChange={handleFileChange}
          />

          {hasFiles && (
            <ChatInputAttachmentList
              files={files}
              allowImages={allowImages}
              imagePreviewUrls={imagePreviewUrls}
              attachmentsWithinLimit={attachmentsWithinLimit}
              onRemoveFile={handleRemoveFile}
              className="px-4"
            />
          )}

          {textarea}
          <div className="min-h-0 min-w-0 flex-1" aria-hidden />
          {composerBottomRow}
        </div>
        {isConversation ? (
          <p
            className="mt-2 px-1 text-center text-xxs leading-4 text-gray-500"
            role="note"
          >
            {CONVERSATION_AI_DISCLAIMER}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ChatInput;
export type { BriefingModeMenuProps } from "./BriefingModeMenu";
